// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useEffect, useState } from 'react';
import type { ToastMessage } from '@/hooks/useToast';

/* ─── Single Toast ─── */
interface ToastProps extends ToastMessage {
  onClose: () => void;
  translate?: typeof tr;
}

const CONFIGS = {
  success: {
    icon: 'ri-checkbox-circle-fill',
    iconColor: 'text-emerald-500',
    border: 'border-emerald-200',
    bg: 'bg-white',
    bar: 'bg-emerald-500',
    titleColor: 'text-emerald-800',
  },
  error: {
    icon: 'ri-error-warning-fill',
    iconColor: 'text-red-500',
    border: 'border-red-200',
    bg: 'bg-white',
    bar: 'bg-red-500',
    titleColor: 'text-red-800',
  },
  warning: {
    icon: 'ri-alert-fill',
    iconColor: 'text-amber-500',
    border: 'border-amber-200',
    bg: 'bg-white',
    bar: 'bg-amber-500',
    titleColor: 'text-amber-800',
  },
  info: {
    icon: 'ri-information-fill',
    iconColor: 'text-teal-500',
    border: 'border-teal-200',
    bg: 'bg-white',
    bar: 'bg-teal-500',
    titleColor: 'text-teal-800',
  },
};

function SingleToast({ type, title, message, duration = 4000, action, onClose, translate = tr }: ToastProps) {
  useStoreLanguage();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const cfg = CONFIGS[type] ?? CONFIGS.info;

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 50);

    const closeTimer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  return (
    <div
      className={`w-80 ${cfg.bg} border ${cfg.border} rounded-xl overflow-hidden transition-all duration-300 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
      }`}
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
    >
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <i className={`${cfg.icon} text-xl ${cfg.iconColor}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          {translate(title && (
            <p className={`text-sm font-bold ${cfg.titleColor} leading-tight`}>{translate(title)}</p>
          ))}
          <p className="text-sm text-gray-600 mt-0.5 leading-snug">{translate(message)}</p>
          {translate(action && (
            <button
              onClick={() => { action.onClick(); onClose(); }}
              className={`mt-2 text-xs font-bold ${cfg.iconColor} hover:underline cursor-pointer whitespace-nowrap`}
            >
              {translate(action.label)} →
            </button>
          ))}
        </div>
        <button
          onClick={() => { setVisible(false); setTimeout(onClose, 300); }}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-md hover:bg-gray-100"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-0.5 bg-gray-100">
        <div
          className={`h-full ${cfg.bar} transition-all duration-75`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/* ─── Toast Container ─── */
export interface ToastContainerProps {
  toasts: ToastMessage[];
  onRemove: (id: string) => void;
  translate?: typeof tr;
}

export function ToastContainer({ toasts, onRemove, translate = tr }: ToastContainerProps) {
  useStoreLanguage();
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 items-end">
      {tr(toasts.map((t) => (
        <SingleToast key={t.id} {...t} translate={translate} onClose={() => onRemove(t.id)} />
      )))}
    </div>
  );
}

/* ─── Legacy default export (backward compat) ─── */
export interface ToastProps2 {
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  message: string;
  duration?: number;
  onClose: () => void;
}

const Toast = ({ type, title, message, duration, onClose }: ToastProps2) => { useStoreLanguage(); return (
  <SingleToast
    id="legacy"
    type={type}
    title={tr(title)}
    message={tr(message)}
    duration={duration}
    onClose={onClose}
  />
); };

export default Toast;
